import React, { useEffect, useRef, useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { toast } from 'react-toastify'
import {
    isPaymentGatewayExpired,
    isPaymentGatewaySucceeded,
    paymentGatewayExpiresAt,
} from '../../utils/paymentGateway'

const POLL_INTERVAL_MS = 4000

const formatRemaining = (milliseconds) => {
    const seconds = Math.max(0, Math.ceil(milliseconds / 1000))
    return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`
}

const safeFileName = (value) => `${value || 'payment'}`
    .replace(/[^a-z0-9_-]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80) || 'payment'

const triggerDownload = (href, filename) => {
    const link = document.createElement('a')
    link.href = href
    link.download = filename
    link.style.display = 'none'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
}

const whatsappUrl = (phone) => {
    const raw = `${phone || ''}`.replace(/[^0-9]/g, '')
    if (!raw) return null
    const normalized = raw.startsWith('0') ? `62${raw.slice(1)}` : raw
    return /^62\d{8,14}$/.test(normalized) ? `https://wa.me/${normalized}` : null
}

function QrisPaymentModal ({ payment, storePhone, onClose, onFetchStatus, onSuccess, onExpired }) {
    const qrContainerRef = useRef(null)
    const pollingRef = useRef(false)
    const expiryHandledRef = useRef(false)
    const expiry = paymentGatewayExpiresAt(payment?.gateway)
    const expiryMs = expiry ? Date.parse(expiry) : null
    const [now, setNow] = useState(Date.now())
    const [providerStatus, setProviderStatus] = useState(payment?.gateway?.status || null)
    const [checking, setChecking] = useState(false)
    const [downloadBusy, setDownloadBusy] = useState(false)
    const locallyExpired = Number.isFinite(expiryMs) && now >= expiryMs
    const succeeded = isPaymentGatewaySucceeded({ status: providerStatus })
    const expired = locallyExpired || isPaymentGatewayExpired({ status: providerStatus })

    useEffect(() => {
        // The component can remain mounted when a customer resumes another
        // payment, so reset the one-time expiry action for a new request.
        expiryHandledRef.current = false
        setProviderStatus(payment?.gateway?.status || null)
        setNow(Date.now())
    }, [payment?.gateway?.payment_request_id, payment?.gateway?.reference_id])

    useEffect(() => {
        const timer = window.setInterval(() => setNow(Date.now()), 1000)
        return () => window.clearInterval(timer)
    }, [])

    useEffect(() => {
        if (!succeeded) return undefined
        const timer = window.setTimeout(() => onSuccess?.(), 2500)
        return () => window.clearTimeout(timer)
    }, [succeeded, onSuccess])

    useEffect(() => {
        if (!expired || succeeded) return
        if (expiryHandledRef.current) return
        expiryHandledRef.current = true
        onExpired?.()
    }, [expired, succeeded, onExpired])

    useEffect(() => {
        if (!onFetchStatus || succeeded || expired || isPaymentGatewayExpired({ status: providerStatus })) return undefined

        let active = true
        const poll = async () => {
            if (pollingRef.current || !active) return
            pollingRef.current = true
            setChecking(true)
            try {
                const gateway = await onFetchStatus()
                if (active && gateway?.status) setProviderStatus(gateway.status)
            } catch (error) {
                // Network errors should not imply an unsuccessful payment.
            } finally {
                if (active) setChecking(false)
                pollingRef.current = false
            }
        }
        poll()
        const timer = window.setInterval(poll, POLL_INTERVAL_MS)
        return () => {
            active = false
            window.clearInterval(timer)
        }
    }, [onFetchStatus, providerStatus, succeeded, expired])

    const downloadQr = async () => {
        if (expired) {
            toast.error('QR Code sudah kedaluwarsa dan tidak dapat diunduh')
            return
        }
        if (downloadBusy) return
        const svgElement = qrContainerRef.current?.querySelector('svg')
        if (!svgElement) {
            console.error('[QRIS] QR SVG element is unavailable for download')
            toast.error('QR Code belum siap untuk diunduh')
            return
        }
        setDownloadBusy(true)
        let source = ''
        let objectUrl = null
        const filename = `qris-${safeFileName(payment?.transaction?.transaction_number || payment?.transaction?.transaction_id)}.png`
        try {
            source = new XMLSerializer().serializeToString(svgElement)
            if (!/xmlns=/.test(source)) source = source.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"')
            source = `<?xml version="1.0" encoding="UTF-8"?>${source}`
            const blob = new Blob([source], { type: 'image/svg+xml;charset=utf-8' })
            objectUrl = URL.createObjectURL(blob)
            const image = new Image()
            await new Promise((resolve, reject) => {
                image.onload = resolve
                image.onerror = reject
                image.src = objectUrl
            })
            const canvas = document.createElement('canvas')
            canvas.width = 512
            canvas.height = 512
            const context = canvas.getContext('2d')
            context.fillStyle = '#ffffff'
            context.fillRect(0, 0, canvas.width, canvas.height)
            context.drawImage(image, 0, 0, canvas.width, canvas.height)
            triggerDownload(canvas.toDataURL('image/png'), filename)
            toast.success('QR Code berhasil diunduh')
        } catch (error) {
            console.error('[QRIS] PNG download failed; downloading SVG fallback', error)
            try {
                const fallbackUrl = URL.createObjectURL(new Blob([source], { type: 'image/svg+xml;charset=utf-8' }))
                triggerDownload(fallbackUrl, filename.replace(/\.png$/i, '.svg'))
                window.setTimeout(() => URL.revokeObjectURL(fallbackUrl), 1000)
                toast.success('QR Code berhasil diunduh sebagai SVG')
            } catch (fallbackError) {
                console.error('[QRIS] SVG fallback download failed', fallbackError)
                toast.error('Gagal mengunduh QR Code')
            }
        } finally {
            if (objectUrl) URL.revokeObjectURL(objectUrl)
            setDownloadBusy(false)
        }
    }

    const remaining = Number.isFinite(expiryMs) ? formatRemaining(expiryMs - now) : '--:--'
    // For split payments, QRIS represents only the gateway remainder. The
    // transaction total remains the complete order value and must not be shown
    // as the amount to scan.
    const configuredAmount = Number(payment?.amount)
    const amount = Number.isFinite(configuredAmount) && configuredAmount >= 0
        ? configuredAmount
        : Number(payment?.transaction?.gateway_charge_amount || payment?.transaction?.transaction_total_amount || 0)
    const supportUrl = whatsappUrl(storePhone || payment?.transaction?.store?.store_phone || payment?.transaction?.store_phone)

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
            <div className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-xl">
                {succeeded ? (
                    <>
                        <h2 className="text-lg font-bold text-emerald-700">Terima kasih telah melakukan pembayaran</h2>
                        <p className="mt-2 text-sm text-slate-600">Pembayaran Anda sudah kami terima. Mengarahkan ke daftar transaksi…</p>
                    </>
                ) : expired ? (
                    <>
                        <h2 className="text-lg font-bold text-rose-700">Waktu pembayaran telah habis</h2>
                        <p className="mt-2 text-sm text-slate-600">Silakan kembali ke daftar transaksi untuk melihat status terbaru.</p>
                    </>
                ) : (
                    <>
                        <h2 className="text-lg font-bold text-slate-700">Bayar dengan QRIS</h2>
                        <p className="mt-2 text-sm text-slate-600">Scan QR dengan aplikasi mobile banking atau e-wallet Anda.</p>
                        <p className="mt-3 text-2xl font-bold tabular-nums text-rose-600">{remaining}</p>
                        <p className="text-xs text-slate-500">Sisa waktu pembayaran</p>
                        <div ref={qrContainerRef} className="mt-5 flex justify-center rounded-xl border bg-white p-4">
                            <QRCodeSVG value={payment.value} size={240} level="M" includeMargin />
                        </div>
                        <p className="mt-4 text-lg font-bold text-slate-700">Rp {Intl.NumberFormat('en-ID').format(amount)}</p>
                        <p className="mt-2 text-xs text-slate-500">Status diperbarui otomatis setelah konfirmasi dari XENDIT{checking ? '…' : '.'}</p>
                        <p className="mt-3 text-xs leading-relaxed text-slate-600">
                            Mengalami kendala pembayaran atau status masih pending setelah membayar?{' '}
                            {supportUrl ? <a href={supportUrl} target="_blank" rel="noopener noreferrer" className="font-semibold text-blue-600 underline">Hubungi kami di sini</a> : 'Hubungi toko kami.'}
                        </p>
                        <button type="button" className="btn mt-4 w-full border-none bg-slate-700 text-white" onClick={downloadQr} disabled={downloadBusy}>
                            {downloadBusy ? 'Menyiapkan QR Code…' : 'Download QR Code'}
                        </button>
                    </>
                )}
                <button type="button" className={`btn mt-3 w-full border-none ${expired || succeeded ? 'bg-slate-200 text-white' : 'bg-slate-700 text-white'}`} onClick={onClose}>
                    {expired || succeeded ? 'Kembali ke daftar transaksi' : 'Tutup'}
                </button>
            </div>
        </div>
    )
}

export default QrisPaymentModal
